import { createRoot } from 'react-dom/client';
import { runtime } from '@repo/env';
import { TELEMETRY_COMPONENTS } from '@repo/telemetry';
import { startBrowserTelemetry } from '@repo/telemetry/browser';

import { TemplateBuilderApp } from './app';
// SPA-only global styles (html/body/#root height). The library bundle must not
// ship these, so this import lives in the standalone entry, not in `app.tsx`.
import './standalone.css';
import { getAuthToken } from './lib/auth/token-store';
import type { TemplateBuilderConfig } from './lib/config/template-builder-config';
import {
  getBackendUrl,
  getFeatureFeedbackAccountToken,
  getFeatureFeedbackApiHost,
  getOcRegistryUrl,
  getRotagenUrl,
} from './lib/env';
import { DevLanguageSwitcher } from './locale/dev-language-switcher';
import { TemplateBuilderProvider } from './template-builder-provider';

/**
 * SPA entry point — mounts the app into `#root` for `yarn dev:isolate`.
 *
 * Dev mirrors the WFM library embed: it builds a `TemplateBuilderConfig`
 * (URLs and environment from the dev env, a dev bearer from `VITE_DEV_TOKEN`
 * via the token store, a dev user, console telemetry) and renders through
 * `TemplateBuilderProvider` — the exact contract WFM uses with real values.
 * No `window.AccessEvo`: the app reads everything from the config registry
 * now.
 */
function buildDevConfig(): TemplateBuilderConfig {
  return {
    environment: runtime.environment,
    backendApiUrl: getBackendUrl(),
    rotagenApiUrl: getRotagenUrl(),
    auth: {
      getAccessToken: () => Promise.resolve(getAuthToken()),
      refreshAccessToken: () => Promise.resolve(getAuthToken()),
    },
    locale: 'en',
    telemetry: {
      trackEvent: (name, properties) => console.debug('[telemetry] event:', name, properties),
      trackError: (error, properties) => console.error('[telemetry] error:', error, properties),
    },
    user: { id: 'dev', emailAddress: 'dev@local', organisation: { id: 'dev-org' } },
    feedback: {
      ocRegistryUrl: getOcRegistryUrl(),
      apiHost: getFeatureFeedbackApiHost(),
      accountToken: getFeatureFeedbackAccountToken(),
    },
  };
}

const rootElement = document.getElementById('root');
if (rootElement) {
  // Coralogix RUM is standalone-only: the library entry never starts it
  // (ENG-6693). A silent no-op without a RUM key (uat and production get
  // one). Sessions stay anonymous: the SPA has no real login, so its dev user
  // is not attached as the session identity.
  startBrowserTelemetry({
    component: TELEMETRY_COMPONENTS.templateBuilderEvo,
    environment: runtime.environment,
    version: __APP_VERSION__,
    tracingUrls: [getBackendUrl(), getRotagenUrl()],
    publicKey: import.meta.env.VITE_CORALOGIX_RUM_PUBLIC_KEY ?? '',
  });

  createRoot(rootElement).render(
    <TemplateBuilderProvider config={buildDevConfig()}>
      <TemplateBuilderApp />
      {/* Standalone-only dev affordance for walking the surface per locale.
          It lives in the SPA entry (not the shared `TemplateBuilderApp`) so it
          never ships in the library bundle — a host (WFM) drives locale via
          `config.locale` and the `access-evo-change-language` event instead. */}
      {import.meta.env.DEV ? <DevLanguageSwitcher /> : null}
    </TemplateBuilderProvider>,
  );
}
